import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Icon } from "../../lib/icons";
import { Button } from "../Button";
import { Link } from "../Link";
import { cn } from "../../lib/utils";

const toastVariants = cva(
  "relative flex items-center gap-3 p-4 rounded-large shadow-lg min-w-[360px] max-w-[480px] transition-all",
  {
    variants: {
      type: {
        information: "",
        promotional: "bg-surface-fill-neutral-intense",
      },
      intent: {
        neutral: "",
        positive: "",
        negative: "",
        notice: "",
        info: "",
      },
    },
    compoundVariants: [
      // Information type with different intents
      {
        type: "information",
        intent: "neutral",
        class: "bg-feedback-fill-neutral-intense",
      },
      {
        type: "information",
        intent: "positive",
        class: "bg-feedback-fill-positive-intense",
      },
      {
        type: "information",
        intent: "negative",
        class: "bg-feedback-fill-negative-intense",
      },
      {
        type: "information",
        intent: "notice",
        class: "bg-feedback-fill-notice-intense",
      },
      {
        type: "information",
        intent: "info",
        class: "bg-feedback-fill-info-intense",
      },
    ],
    defaultVariants: {
      type: "information",
      intent: "neutral",
    },
  }
);

const toastIconVariants = cva(
  "flex-shrink-0 flex items-center justify-center",
  {
    variants: {
      type: {
        information: "text-white",
        promotional: "text-action-ink-neutral-normal",
      },
    },
    defaultVariants: {
      type: "information",
    },
  }
);

const toastTextVariants = cva("text-body-small-regular", {
  variants: {
    type: {
      information: "text-white",
      promotional: "text-surface-ink-neutral-normal",
    },
  },
  defaultVariants: {
    type: "information",
  },
});

const toastLinkVariants = cva(
  "text-body-small-medium cursor-pointer hover:underline",
  {
    variants: {
      type: {
        information: "text-white",
        promotional:
          "text-action-ink-primary-normal hover:text-action-ink-primary-subtle",
      },
    },
    defaultVariants: {
      type: "information",
    },
  }
);

const toastCloseButtonVariants = cva(
  "flex-shrink-0 cursor-pointer hover:opacity-75 transition-opacity",
  {
    variants: {
      type: {
        information: "text-white",
        promotional: "text-action-ink-neutral-subtle",
      },
    },
    defaultVariants: {
      type: "information",
    },
  }
);

export interface ToastProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title" | "id"> {
  /**
   * Unique identifier for the toast
   */
  id?: string;
  /**
   * Type of toast - determines behavior and styling
   * - information: Auto-dismisses after duration, colored background (default position: bottom-center)
   * - promotional: Stays until user dismisses, white background with more content (default position: bottom-right)
   */
  type?: "information" | "promotional";
  /**
   * Intent/theme of the toast (only applicable for information type)
   */
  intent?: "neutral" | "positive" | "negative" | "notice" | "info";
  /**
   * Position of the toast
   * - information type default: bottom-center
   * - promotional type default: bottom-right
   */
  position?:
    | "top-left"
    | "top-center"
    | "top-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";
  /**
   * Icon to display. If not provided, an icon will be automatically selected based on intent
   */
  icon?: React.ReactNode;
  /**
   * Main message content (required for information type)
   */
  message?: string;
  /**
   * Optional heading (primarily for promotional type)
   */
  heading?: string;
  /**
   * Optional description (primarily for promotional type)
   */
  description?: string;
  /**
   * Link text to display on the right
   */
  linkText?: string;
  /**
   * Callback fired when the link is clicked
   */
  onLinkClick?: () => void;
  /**
   * Button text (for promotional type)
   */
  buttonText?: string;
  /**
   * Callback fired when the button is clicked
   */
  onButtonClick?: () => void;
  /**
   * Callback fired when the close button is clicked
   */
  onClose?: () => void;
  /**
   * Duration in milliseconds before auto-dismiss (only for information type)
   * @default 5000
   */
  duration?: number;
  /**
   * Whether to show the toast
   */
  isVisible?: boolean;
  /**
   * Custom class name
   */
  className?: string;
}

const getDefaultIcon = (
  type: ToastProps["type"],
  intent: ToastProps["intent"]
): React.ReactNode => {
  const iconSize = 16;

  if (type === "promotional") {
    return <Icon name="info" size={iconSize} aria-hidden="true" />;
  }

  // Information type icons based on intent
  switch (intent) {
    case "positive":
      return <Icon name="check" size={iconSize} aria-hidden="true" />;
    case "negative":
      return <Icon name="exclamation" size={iconSize} aria-hidden="true" />;
    case "notice":
      return <Icon name="info" size={iconSize} aria-hidden="true" />;
    case "info":
      return <Icon name="info" size={iconSize} aria-hidden="true" />;
    case "neutral":
    default:
      return <Icon name="check" size={iconSize} aria-hidden="true" />;
  }
};

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      className,
      type = "information",
      intent = "neutral",
      icon,
      message,
      heading,
      description,
      linkText,
      onLinkClick,
      buttonText,
      onButtonClick,
      onClose,
      duration = 5000,
      isVisible = true,
      ...props
    },
    ref
  ) => {
    const [visible, setVisible] = React.useState(isVisible);
    const timerRef = React.useRef<number | null>(null);

    React.useEffect(() => {
      setVisible(isVisible);
    }, [isVisible]);

    React.useEffect(() => {
      // Auto-dismiss only for information type
      if (type === "information" && visible && duration > 0) {
        timerRef.current = window.setTimeout(() => {
          setVisible(false);
          onClose?.();
        }, duration);
      }

      return () => {
        if (timerRef.current) {
          window.clearTimeout(timerRef.current);
        }
      };
    }, [type, visible, duration, onClose]);

    const handleClose = () => {
      setVisible(false);
      onClose?.();
    };

    const displayIcon = icon ?? getDefaultIcon(type, intent);

    if (!visible) {
      return null;
    }

    // Promotional type layout
    if (type === "promotional") {
      return (
        <div
          ref={ref}
          className={cn(
            toastVariants({ type, intent }),
            "flex-row items-start gap-3",
            className
          )}
          role="alert"
          {...props}
        >
          <div className="py-1">
            <div
              className={cn(toastIconVariants({ type }), "h-5 w-5 shrink-0")}
            >
              {displayIcon}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex flex-row items-center justify-between">
              <div className="flex-1 min-w-0">
                {heading && (
                  <div className="text-body-small-semibold text-surface-ink-neutral-normal">
                    {heading}
                  </div>
                )}
              </div>
              {onClose && (
                <div
                  className={cn(toastCloseButtonVariants({ type }), "h-5 w-5")}
                  onClick={handleClose}
                  role="button"
                  aria-label="Close toast"
                >
                  <Icon name="close" size={16} />
                </div>
              )}
            </div>
            {/* Description */}
            {description && (
              <div className="text-body-small-regular text-surface-ink-neutral-subtle">
                {description}
              </div>
            )}
            {/* Button */}
            {buttonText && (
              <div>
                <Button
                  variant="secondary"
                  color="primary"
                  size="xsmall"
                  onClick={onButtonClick}
                >
                  {buttonText}
                </Button>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Information type layout
    return (
      <div
        ref={ref}
        className={cn(toastVariants({ type, intent }), className)}
        role="alert"
        {...props}
      >
        {/* Icon */}
        <div className={cn(toastIconVariants({ type }), "h-5 w-5 shrink-0")}>
          {displayIcon}
        </div>

        {/* Message */}
        <div className={cn(toastTextVariants({ type }), "flex-1 min-w-0")}>
          {message}
        </div>

        {/* Link (optional) */}
        {linkText && (
          <div
            className={cn(toastLinkVariants({ type }), "shrink-0")}
            onClick={onLinkClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onLinkClick?.();
              }
            }}
          >
            {linkText}
          </div>
        )}

        {/* Close button */}
        {onClose && (
          <div
            className={cn(toastCloseButtonVariants({ type }), "h-5 w-5")}
            onClick={handleClose}
            role="button"
            aria-label="Close toast"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClose();
              }
            }}
          >
            <Icon name="close" size={16} />
          </div>
        )}
      </div>
    );
  }
);

Toast.displayName = "Toast";
